import React, { useEffect, useState } from 'react';
import {
  clearBranchSaleRoundingSettings,
  getCompanySaleRoundingSettings,
  getSaleRoundingSettings,
  updateBranchSaleRoundingSettings,
  updateCompanySaleRoundingSettings,
} from '../api/settingsApi';

const DEFAULT_RULE = { enabled: false, increment: 50, eligiblePaymentMethods: null };
const PAYMENT_METHODS = [
  ['efectivo', 'Efectivo'], ['tarjeta', 'Tarjeta'], ['transferencia', 'Transferencia'],
  ['cheque', 'Cheque'], ['crédito', 'Crédito'],
];

const RuleFields = ({ value, onChange, disabled }) => (
  <fieldset disabled={disabled} className="space-y-4 disabled:opacity-60">
    <label className="flex items-center gap-2 text-sm font-medium">
      <input type="checkbox" checked={value.enabled} onChange={(event) => onChange({ ...value, enabled: event.target.checked })} />
      Activar redondeo hacia abajo
    </label>
    <label className="block text-sm">
      Múltiplo de redondeo
      <select className="mt-1 block w-full rounded border border-gray-300 px-3 py-2" value={value.increment} onChange={(event) => onChange({ ...value, increment: Number(event.target.value) })}>
        {[10, 50, 100].map((increment) => <option key={increment} value={increment}>${increment}</option>)}
      </select>
    </label>
    <label className="block text-sm">
      Aplicar a
      <select className="mt-1 block w-full rounded border border-gray-300 px-3 py-2" value={value.eligiblePaymentMethods === null ? 'all' : 'selected'} onChange={(event) => onChange({ ...value, eligiblePaymentMethods: event.target.value === 'all' ? null : ['efectivo'] })}>
        <option value="all">Todos los medios de pago</option>
        <option value="selected">Medios de pago seleccionados</option>
      </select>
    </label>
    {value.eligiblePaymentMethods !== null && (
      <div className="flex flex-wrap gap-x-4 gap-y-2">
        {PAYMENT_METHODS.map(([method, label]) => (
          <label key={method} className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={value.eligiblePaymentMethods.includes(method)} onChange={(event) => onChange({
              ...value,
              eligiblePaymentMethods: event.target.checked
                ? [...value.eligiblePaymentMethods, method]
                : value.eligiblePaymentMethods.filter((entry) => entry !== method),
            })} />
            {label}
          </label>
        ))}
      </div>
    )}
    <p className="text-xs text-gray-600">Ejemplo: $1.279 → ${Math.floor(1279 / value.increment) * value.increment}. Los múltiplos exactos no cambian. Si el resultado fuera $0, se conserva el total original.</p>
  </fieldset>
);

const validRule = (value) => !value.enabled || value.eligiblePaymentMethods === null || value.eligiblePaymentMethods.length > 0;

const SaleRoundingSettingsPanel = ({ activeBranchId, canManage = false }) => {
  const [companyRule, setCompanyRule] = useState(DEFAULT_RULE);
  const [branchRule, setBranchRule] = useState(DEFAULT_RULE);
  const [overrideEnabled, setOverrideEnabled] = useState(false);
  const [source, setSource] = useState('company');
  const [loading, setLoading] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [branchDirty, setBranchDirty] = useState(false);
  const [saving, setSaving] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const applyBranchSettings = (data) => {
    setCompanyRule(data.companyDefault);
    setBranchRule(data.effective);
    setOverrideEnabled(data.branchOverride !== null);
    setSource(data.source);
    setBranchDirty(false);
  };

  useEffect(() => {
    let active = true;
    setLoading(true);
    setLoaded(false);
    setError('');
    setMessage('');
    const load = async () => {
      try {
        const data = activeBranchId ? await getSaleRoundingSettings(activeBranchId) : await getCompanySaleRoundingSettings();
        if (!active) return;
        if (activeBranchId) applyBranchSettings(data);
        else setCompanyRule(data);
        setLoaded(true);
      } catch (requestError) {
        if (active) setError(requestError?.response?.data?.message || 'No se pudo cargar la configuración de redondeo.');
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => { active = false; };
  }, [activeBranchId, loadAttempt]);

  const save = async (scope) => {
    setSaving(scope);
    setError('');
    setMessage('');
    try {
      if (scope === 'company') {
        setCompanyRule(await updateCompanySaleRoundingSettings(companyRule));
        if (activeBranchId) {
          const data = await getSaleRoundingSettings(activeBranchId);
          if (branchDirty) {
            setCompanyRule(data.companyDefault);
            setSource(data.source);
          } else applyBranchSettings(data);
        }
      } else {
        applyBranchSettings(overrideEnabled
          ? await updateBranchSaleRoundingSettings(activeBranchId, branchRule)
          : await clearBranchSaleRoundingSettings(activeBranchId));
      }
      setMessage(scope === 'company' ? 'Configuración de empresa guardada.' : 'Configuración de sucursal guardada.');
    } catch (requestError) {
      setError(requestError?.response?.data?.message || 'No se pudo guardar la configuración de redondeo.');
    } finally {
      setSaving('');
    }
  };

  if (loading) return <p role="status" className="text-sm text-gray-600">Cargando configuración de redondeo…</p>;
  if (!loaded) return <div className="space-y-3"><p role="alert" className="text-sm text-red-700">{error}</p><button type="button" className="rounded border px-3 py-2 text-sm" onClick={() => setLoadAttempt((attempt) => attempt + 1)}>Reintentar</button></div>;

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600">El descuento se calcula sobre el total final de la venta, después de los demás descuentos e impuestos. Las ventas cerradas conservan su redondeo original.</p>
      <p className="text-xs text-gray-600">En pagos combinados deben estar incluidos todos los medios usados. Las ventas a crédito usan la opción Crédito, aunque tengan un abono inicial.</p>
      {error && <p role="alert" className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</p>}
      {message && <p role="status" className="rounded border border-green-200 bg-green-50 p-3 text-sm text-green-700">{message}</p>}
      <div className="grid gap-4 lg:grid-cols-2">
        <section aria-label="Configuración de empresa" className="space-y-4 rounded-lg border border-gray-200 bg-white p-4">
          <h3 className="font-semibold">Configuración de empresa</h3>
          <p className="text-xs text-gray-600">Se aplica a las sucursales que heredan esta configuración.</p>
          <RuleFields value={companyRule} onChange={setCompanyRule} disabled={!canManage || Boolean(saving)} />
          {canManage && <button type="button" className="rounded bg-blue-600 px-4 py-2 text-sm text-white disabled:opacity-50" disabled={Boolean(saving) || !validRule(companyRule)} onClick={() => save('company')}>{saving === 'company' ? 'Guardando…' : 'Guardar empresa'}</button>}
        </section>
        <section aria-label="Configuración de sucursal" className="space-y-4 rounded-lg border border-gray-200 bg-white p-4">
          <h3 className="font-semibold">Configuración de la sucursal seleccionada</h3>
          {!activeBranchId ? <p className="text-sm text-gray-600">Selecciona una sucursal para configurar su redondeo.</p> : <>
            <p className="text-xs text-gray-600">Configuración vigente: {source === 'branch' ? 'propia de la sucursal' : 'heredada de la empresa'}.</p>
            <label className="flex items-center gap-2 text-sm font-medium">
              <input type="checkbox" checked={overrideEnabled} disabled={!canManage || Boolean(saving)} onChange={(event) => {
                setOverrideEnabled(event.target.checked);
                setBranchDirty(true);
                if (!event.target.checked) setBranchRule(companyRule);
              }} />
              Usar configuración propia para esta sucursal
            </label>
            <RuleFields value={overrideEnabled ? branchRule : companyRule} onChange={(value) => { setBranchRule(value); setBranchDirty(true); }} disabled={!canManage || Boolean(saving) || !overrideEnabled} />
            {canManage && <button type="button" className="rounded bg-blue-600 px-4 py-2 text-sm text-white disabled:opacity-50" disabled={Boolean(saving) || (overrideEnabled && !validRule(branchRule))} onClick={() => save('branch')}>{saving === 'branch' ? 'Guardando…' : overrideEnabled ? 'Guardar sucursal' : 'Usar configuración de empresa'}</button>}
          </>}
        </section>
      </div>
    </div>
  );
};

export default SaleRoundingSettingsPanel;
